if (!customElements.get('wls-blog-content-share')) {
  class WLSBlogContentShare extends HTMLElement {
    constructor() {
      super();
      this.onToggleClick = this._onToggleClick.bind(this);
      this.onDocumentClick = this._onDocumentClick.bind(this);
      this.onKeyDown = this._onKeyDown.bind(this);
      this.onReposition = this._positionDropdown.bind(this);
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this.toggleBtn = this.querySelector('.js-wls-blog-content-share-toggle');
      this.dropdown = this.querySelector('.js-wls-blog-content-share-dropdown');

      if (!this.toggleBtn || !this.dropdown) return;

      this.toggleBtn.addEventListener('click', this.onToggleClick);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeyDown);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      if (this.toggleBtn) {
        this.toggleBtn.removeEventListener('click', this.onToggleClick);
      }

      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('resize', this.onReposition);
      window.removeEventListener('scroll', this.onReposition, true);
    }

    /**
     * Whether the share dropdown is open
     * @returns {boolean}
     */
    get isOpen() {
      return this.toggleBtn?.getAttribute('aria-expanded') === 'true';
    }

    /**
     * Toggle the share dropdown open/closed
     * @param {Event} event - The click event
     * @returns {void}
     */
    _onToggleClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.setOpen(!this.isOpen);
    }

    /**
     * Close the dropdown when clicking outside
     * @param {Event} event - The click event
     * @returns {void}
     */
    _onDocumentClick(event) {
      if (!this.isOpen) return;
      if (this.contains(event.target)) return;
      this.setOpen(false);
    }

    /**
     * Close the dropdown on Escape
     * @param {KeyboardEvent} event - The keydown event
     * @returns {void}
     */
    _onKeyDown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;
      this.setOpen(false);
      this.toggleBtn?.focus();
    }

    /**
     * Set the open state of the dropdown
     * @param {boolean} open - Whether the dropdown should be open
     * @returns {void}
     */
    setOpen(open) {
      if (!this.toggleBtn || !this.dropdown) return;

      this.toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.dropdown.hidden = !open;

      if (open) {
        this._positionDropdown();
        window.addEventListener('resize', this.onReposition);
        window.addEventListener('scroll', this.onReposition, true);
      } else {
        this.dropdown.classList.remove(
          'wls-blog-content__share-dropdown--above',
          'wls-blog-content__share-dropdown--align-right'
        );
        window.removeEventListener('resize', this.onReposition);
        window.removeEventListener('scroll', this.onReposition, true);
      }
    }

    /**
     * Position the dropdown so it stays in viewport
     * Prefers opening above when there is enough space; aligns left unless that overflows
     * @returns {void}
     */
    _positionDropdown() {
      if (!this.toggleBtn || !this.dropdown || this.dropdown.hidden) return;

      this.dropdown.classList.remove(
        'wls-blog-content__share-dropdown--above',
        'wls-blog-content__share-dropdown--align-right'
      );

      const gap = 8;
      const edgePadding = 8;
      const btnRect = this.toggleBtn.getBoundingClientRect();
      const dropdownRect = this.dropdown.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const spaceAbove = btnRect.top - gap;
      const spaceBelow = viewportHeight - btnRect.bottom - gap;
      const openAbove =
        spaceAbove >= dropdownRect.height ||
        (spaceBelow < dropdownRect.height && spaceAbove > spaceBelow);

      if (openAbove) {
        this.dropdown.classList.add('wls-blog-content__share-dropdown--above');
      }

      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

      if (isDesktop) {
        const centerLeft = btnRect.left + btnRect.width / 2 - dropdownRect.width / 2;
        const centerRight = centerLeft + dropdownRect.width;
        if (centerRight > viewportWidth - edgePadding) {
          this.dropdown.classList.add('wls-blog-content__share-dropdown--align-right');
        }
      } else {
        const leftAlignedRightEdge = btnRect.left + dropdownRect.width;
        if (leftAlignedRightEdge > viewportWidth - edgePadding) {
          this.dropdown.classList.add('wls-blog-content__share-dropdown--align-right');
        }
      }
    }
  }

  customElements.define('wls-blog-content-share', WLSBlogContentShare);
}
